import { useCallback, useRef } from 'react';
import { StreetAddress } from '#types';
import { googleMapsApiKey } from '../../../../config';

/**
 * Google Maps Places (New) over REST: autocomplete search, place details, and the session token
 * that groups them for billing. Consumed by `useAddressUI`, which owns the one instance per address
 * field — a second instance would open a second session over the same keystrokes.
 *
 * Both requests carry `sessionTokenRef.current`, read synchronously before their first `await`, so a
 * details call closes the session its predictions were billed under. Nothing here rotates the token:
 * `useAddressUI.handleSelect` calls `endSession` once per selection, of either kind, which is the
 * only place that knows a search has ended.
 */
const useGoogleMapsPlaces = () => {
    const isReady = true; // Always ready for REST API
    const sessionTokenRef = useRef<string>(generateSessionToken());

    const searchAddresses = useCallback(async (query: string): Promise<StreetAddress[]> => {
        if (!googleMapsApiKey || !query || query.length < 3) {
            return [];
        }

        try {
            const url = 'https://places.googleapis.com/v1/places:autocomplete';
            
            const response = await fetch(url, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'X-Goog-Api-Key': googleMapsApiKey
                },
                body: JSON.stringify({
                    input: query,
                    includedPrimaryTypes: ['street_address', 'premise', 'subpremise'],
                    sessionToken: sessionTokenRef.current
                })
            });

            if (!response.ok) {
                console.warn('Places API error:', response.status, await response.text());
                return [];
            }

            const data = await response.json();
            
            if (!data.suggestions) {
                return [];
            }

            return data.suggestions.map((suggestion: any) => ({
                formatted: suggestion.placePrediction?.text?.text || '',
                components: {},
                alternativeData: {
                    placeId: suggestion.placePrediction?.placeId,
                    types: suggestion.placePrediction?.types || []
                }
            }));
        } catch (error) {
            console.error('Address lookup error:', error);
            return [];
        }
    }, []);

    const getPlaceDetails = useCallback(async (placeId: string): Promise<StreetAddress> => {
        if (!googleMapsApiKey) {
            throw new Error('Google Maps API key not configured');
        }

        try {
            const url = `https://places.googleapis.com/v1/places/${placeId}?sessionToken=${encodeURIComponent(sessionTokenRef.current)}`;
            
            const response = await fetch(url, {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                    'X-Goog-Api-Key': googleMapsApiKey,
                    'X-Goog-FieldMask': 'displayName,formattedAddress,addressComponents,location'
                }
            });

            if (!response.ok) {
                throw new Error(`Places API error: ${response.status}`);
            }

            const data = await response.json();

            const components = parseAddressComponents(data.addressComponents || []);
            return {
                formatted: data.formattedAddress || data.displayName?.text || '',
                components,
                alternativeData: { 
                    placeId,
                    location: data.location
                }
            };
        } catch (error) {
            console.error('Place details error:', error);
            throw error;
        }
    }, []);

    /** Ends the current autocomplete session; the next search opens a new one. */
    const endSession = useCallback(() => {
        sessionTokenRef.current = generateSessionToken();
    }, []);

    return {
        isReady,
        searchAddresses,
        getPlaceDetails,
        endSession
    };
};

/**
 * Generates a unique session token for grouping related Places API requests.
 * Used for billing optimization with Google Maps Places API.
 * 
 * @returns Unique session token string
 */
const generateSessionToken = (): string => {
    return `${Date.now()}-${Math.random().toString(36).substring(2, 15)}`;
};

/**
 * Parses Google Maps address components into standardized AddressValue components.
 * Extracts street, city, state, postal code, and country from raw API response.
 *
 * Country and state take `shortText` — the ISO codes the address field stores and Signify requires.
 * Places supplies no recipient, company, phone or email, so those keys are never written here and
 * the caller merges this result over whatever the user already entered.
 *
 * @param components - Array of address components from Google Maps API
 * @returns Parsed address components object
 */
const parseAddressComponents = (components: any[]): StreetAddress['components'] => {
    const parsed: StreetAddress['components'] = {};

    components.forEach((component: any) => {
        const types = component.types || [];
        const longText = component.longText || '';
        const shortText = component.shortText || '';
        
        if (types.includes('street_number')) {
            parsed.street = longText + (parsed.street ? ' ' + parsed.street : '');
        } else if (types.includes('route')) {
            parsed.street = (parsed.street || '') + (parsed.street ? ' ' : '') + longText;
        } else if (types.includes('locality')) {
            parsed.city = longText;
        } else if (types.includes('administrative_area_level_1')) {
            parsed.state = shortText;
        } else if (types.includes('postal_code')) {
            parsed.postalCode = longText;
        } else if (types.includes('country')) {
            parsed.country = shortText;
        }
    });

    if (parsed.street) {
        parsed.street = parsed.street.trim();
    }

    return parsed;
};

export default useGoogleMapsPlaces;
